import { useMemo } from 'react';
import { useAsync } from 'react-use';

import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/** The most probable tag values for a subscription, as `engineering-insights` reports them. */
export type SubscriptionTags = {
  stage: string;
  application: string;
  deployer: string;
};

export type SubscriptionTagSuggestions = {
  /** Suggested tags keyed by lowercased subscription id. */
  bySubscription: Record<string, SubscriptionTags>;
  loading: boolean;
};

const EMPTY: Record<string, SubscriptionTags> = {};

/**
 * Looks up the suggested tag values for each subscription. A suggestion is only
 * a convenience, so a subscription whose lookup fails is left out instead of
 * failing the rest.
 */
export const useSubscriptionTagSuggestions = (
  subscriptionIds: string[],
  enabled = true,
): SubscriptionTagSuggestions => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const uniqueIds = useMemo(
    () =>
      Array.from(new Set(subscriptionIds.map(id => id.toLowerCase()))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [subscriptionIds],
  );
  // The ids a result belongs to. `useAsync` only starts a new lookup after the
  // render in which they change, so until then the previous result is stale.
  const requestKey = enabled ? uniqueIds.join(',') : '';

  const { value } = useAsync(async () => {
    if (!requestKey) return { requestKey, bySubscription: EMPTY };

    let baseUrl: string;
    try {
      baseUrl = await discoveryApi.getBaseUrl('engineering-insights');
    } catch {
      return { requestKey, bySubscription: EMPTY };
    }

    const entries = await Promise.all(
      requestKey.split(',').map(async id => {
        try {
          const response = await fetchApi.fetch(
            `${baseUrl}/application-tags/${encodeURIComponent(id)}`,
          );
          if (!response.ok) return undefined;
          return [id, (await response.json()) as SubscriptionTags] as const;
        } catch {
          return undefined;
        }
      }),
    );

    return {
      requestKey,
      bySubscription: Object.fromEntries(
        entries.filter(
          (entry): entry is readonly [string, SubscriptionTags] => !!entry,
        ),
      ),
    };
  }, [discoveryApi, fetchApi, requestKey]);

  const current = value?.requestKey === requestKey;
  return {
    bySubscription: current ? value.bySubscription : EMPTY,
    loading: !!requestKey && !current,
  };
};
